import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Paperclip, Send } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  HtmlFrame,
  Input,
  Label,
  Skeleton,
  toast,
} from '@blimo/ui';
import { previousMonth, REPORT_FLAGS } from './utils.js';

const REASONS = {
  account_not_usable: 'Konto nicht aktiv',
  no_finance_access: 'kein Investor-/Finanzzugang',
  no_email: 'keine E-Mail-Adresse',
  opted_out: 'hat widersprochen',
  no_units: 'keine Einheiten sichtbar',
};

const DISPATCH = { sent: ['success', 'versendet'], skipped: ['neutral', 'übersprungen'], failed: ['destructive', 'fehlgeschlagen'] };

/**
 * Monatsberichte (F-018): flags, preview (recipients + rendered mail) and "send now". Monthly dispatch runs automatically on the 1st.
 * @param {{ client: { id: number, reports: Record<string, boolean>|null }, canEdit: boolean }} props
 */
export function ReportsTab({ client, canEdit }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(previousMonth());
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(null);
  const flags = client.reports ?? {};
  const preview = useQuery(adminQueries.mandateReportPreview(api, client.id, { month }));
  const active = REPORT_FLAGS.filter((f) => flags[f.key]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.admin.detail('mandates', client.id) }),
      queryClient.invalidateQueries({ queryKey: keys.admin.sub('mandates', client.id, 'report-preview') }),
    ]);

  async function toggle(key, value) {
    setSaving(key);
    try {
      await api.admin.mandates.updateReports(client.id, { [key]: value });
      await refresh();
      toast.success('Monatsbericht aktualisiert');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Einstellung konnte nicht gespeichert werden.'));
    } finally {
      setSaving(null);
    }
  }

  async function send() {
    try {
      const result = await api.admin.mandates.sendReport(client.id, { month });
      const count = (s) => result.results.filter((r) => r.status === s).length;
      toast.success(`Monatsbericht: ${count('sent')} versendet, ${count('already_sent')} bereits versendet, ${count('skipped')} übersprungen`);
      await refresh();
    } catch (e) {
      toast.error(
        hasProblemType(e, 'client-reports-disabled') ? 'Kein Berichtsinhalt aktiviert.' : getErrorMessage(e, 'Der Bericht konnte nicht versendet werden.'),
      );
    }
  }

  const data = preview.data;
  const eligible = (data?.recipients ?? []).filter((r) => r.eligible);

  return (
    <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle as="h2">Inhalte</CardTitle>
          <CardDescription>Versand automatisch am 1. des Monats (07:00) für den Vormonat an die Investoren-Mitglieder.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {REPORT_FLAGS.map((f) => (
            <label key={f.key} className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={Boolean(flags[f.key])}
                disabled={!canEdit || saving !== null}
                onChange={(e) => toggle(f.key, e.target.checked)}
                aria-describedby={`${f.key}-hint`}
              />
              <span>
                <span className="font-medium">{f.label}</span>
                <span id={`${f.key}-hint`} className="block text-xs text-muted-foreground">
                  {f.hint}
                </span>
              </span>
            </label>
          ))}
          <div className="mt-2 flex flex-col gap-1.5 border-t pt-3">
            <Label htmlFor="report-month">Berichtsmonat</Label>
            <Input id="report-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" />
          </div>
          {canEdit && (
            <Button size="sm" className="self-start" disabled={active.length === 0 || eligible.length === 0} onClick={() => setConfirm(true)}>
              <Send aria-hidden="true" /> Bericht jetzt senden
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Jeder Empfänger erhält nur Werte seiner freigegebenen Einheiten und Kostenkonten. Bereits versendete Berichte eines Monats werden nicht erneut
            gesendet.
          </p>
        </CardContent>
      </Card>

      <div className="flex min-w-0 flex-col gap-3">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Empfänger</CardTitle>
          </CardHeader>
          <CardContent>
            {preview.error ? (
              <ErrorState compact error={preview.error} onRetry={() => preview.refetch()} />
            ) : preview.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : data.recipients.length === 0 ? (
              <EmptyState compact icon={Mail} title="Keine Mitglieder mit Benutzerkonto" />
            ) : (
              <ul className="divide-y text-sm" aria-label="Empfänger des Monatsberichts">
                {data.recipients.map((r) => (
                  <li key={r.user_id} className="flex flex-wrap items-center gap-2 py-2">
                    <span className="font-medium">{r.name}</span>
                    <span className="text-muted-foreground">{r.email}</span>
                    <span className="ms-auto flex items-center gap-2">
                      {r.eligible ? <Badge variant="success">erhält Bericht</Badge> : <Badge variant="neutral">{REASONS[r.reason] ?? r.reason}</Badge>}
                      {r.dispatch && (
                        <Badge
                          variant={DISPATCH[r.dispatch.status]?.[0] ?? 'neutral'}
                          title={r.dispatch.sent_at ? formatDateTime(r.dispatch.sent_at) : undefined}
                        >
                          {DISPATCH[r.dispatch.status]?.[1] ?? r.dispatch.status}
                          {r.dispatch.sent_at && ` ${formatDateTime(r.dispatch.sent_at)}`}
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Vorschau</CardTitle>
            {data?.mail && <CardDescription>{data.mail.subject}</CardDescription>}
          </CardHeader>
          <CardContent>
            {preview.isPending ? (
              <Skeleton className="h-40 w-full" />
            ) : !data?.mail ? (
              <p className="text-sm text-muted-foreground">
                {active.length === 0 ? 'Kein Berichtsinhalt aktiviert.' : 'Keine berechtigten Empfänger – keine Vorschau möglich.'}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                <HtmlFrame html={data.mail.body_html} title="Vorschau Monatsbericht" />
                {data.mail.attachment && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Paperclip aria-hidden="true" className="size-3.5" /> {data.mail.attachment.name}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {canEdit && (
        <ConfirmDialog
          open={confirm}
          onOpenChange={setConfirm}
          title="Monatsbericht jetzt senden?"
          description={`Bericht für ${month} an ${eligible.length} Empfänger. Bereits versendete Berichte dieses Monats werden übersprungen.`}
          confirmLabel="Senden"
          onConfirm={send}
        />
      )}
    </div>
  );
}
